import React from 'react';
import { Heart, Phone, ArrowRight, CheckCircle, AlertTriangle, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const DepressionTreatmentPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Depression Treatment in Maryland"
        description="Help with depression in Pikesville and Baltimore County, MD. SDORA Wellbeing offers outpatient therapy, medication management, and psychiatric rehabilitation for adults living with depression. Accepting Medicaid."
        path="/resources/depression-treatment"
        type="article"
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-indigo-700 to-blue-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Depression Treatment in Maryland
          </h1>
          <p className="text-lg text-indigo-200 max-w-2xl mx-auto">
            You are not alone. Depression is treatable, and help is available close to home in Pikesville and throughout Baltimore County.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Understanding Depression */}
        <section className="mb-12" data-testid="depression-understanding-section">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Understanding Depression</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Depression is more than feeling sad. It is a medical condition that affects how you think, feel, and handle daily activities.
            Symptoms can include persistent sadness, loss of interest in things you used to enjoy, fatigue, difficulty concentrating,
            changes in sleep or appetite, and feelings of worthlessness or hopelessness.
          </p>
          <p className="text-gray-700 leading-relaxed">
            According to the National Institute of Mental Health, major depressive disorder is one of the most common mental health
            conditions in the United States. The good news is that effective treatments exist, and most people who receive appropriate
            care experience meaningful improvement.
          </p>
        </section>

        {/* Warning Signs */}
        <section className="mb-12 bg-amber-50 rounded-xl p-6 border border-amber-100" data-testid="depression-warning-signs">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">When to Seek Help</h2>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            Consider reaching out to a mental health professional if you experience any of the following for two weeks or more:
          </p>
          <ul className="space-y-2">
            {[
              "Persistent feelings of sadness, emptiness, or hopelessness",
              "Loss of interest or pleasure in activities you previously enjoyed",
              "Significant changes in appetite or weight",
              "Sleeping too much or too little",
              "Fatigue or lack of energy nearly every day",
              "Difficulty concentrating, remembering, or making decisions",
              "Withdrawal from friends, family, or social activities",
              "Thoughts of death or suicide",
            ].map((sign) => (
              <li key={sign} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 flex-shrink-0" />
                {sign}
              </li>
            ))}
          </ul>
          <p className="text-sm text-gray-700 mt-4 font-medium">
            If you or someone you know is in crisis, call or text 988 (Suicide & Crisis Lifeline) or go to your nearest emergency room.
          </p>
        </section>

        {/* Treatment Approaches */}
        <section className="mb-12" data-testid="depression-treatments">
          <h2 className="text-lg font-bold text-gray-900 mb-4">How SDORA Wellbeing Treats Depression</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            At SDORA Wellbeing, we take a whole-person approach to depression treatment. Our licensed clinicians create
            individualized treatment plans based on your unique needs, goals, and circumstances. Treatment may include
            one or more of the following:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: "Individual Therapy (OMHC)",
                desc: "One-on-one sessions with a licensed therapist using evidence-based approaches including cognitive behavioral therapy (CBT) and supportive counseling.",
                link: "/services/outpatient-mental-health",
              },
              {
                title: "Medication Management",
                desc: "Psychiatric evaluation and ongoing medication support for antidepressants or other prescribed treatments, with regular monitoring.",
                link: "/services/medication-management",
              },
              {
                title: "Psychiatric Rehabilitation (PRP)",
                desc: "Skill-building support to help you manage symptoms, rebuild daily routines, and re-engage with your community.",
                link: "/services/psychiatric-rehabilitation",
              },
              {
                title: "Case Management",
                desc: "Care coordination to connect you with housing, benefits, transportation, and other resources that support your recovery.",
                link: "/services/case-management",
              },
            ].map((item) => (
              <div key={item.title} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <h3 className="font-semibold text-gray-900 text-sm mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 mb-2">{item.desc}</p>
                <Link to={item.link} className="text-sm text-indigo-600 font-medium hover:underline inline-flex items-center gap-1">
                  Learn more <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* What to Expect */}
        <section className="mb-12" data-testid="depression-what-to-expect">
          <h2 className="text-lg font-bold text-gray-900 mb-4">What to Expect</h2>
          <div className="space-y-4">
            {[
              { step: "1", title: "Initial Assessment", desc: "A comprehensive evaluation to understand your symptoms, history, and goals. This helps your clinician recommend the right combination of services." },
              { step: "2", title: "Individualized Treatment Plan", desc: "Together with your clinician, you will create a plan that outlines specific treatment goals and the services that will help you reach them." },
              { step: "3", title: "Ongoing Support", desc: "Regular therapy sessions, medication check-ins, or rehabilitation support — adjusted over time as you progress toward recovery." },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-4">
                <div className="w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Telehealth */}
        <section className="mb-12 bg-blue-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-start gap-3 mb-3">
            <BookOpen className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <h2 className="text-lg font-bold text-gray-900">Telehealth Available</h2>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            Many of our depression treatment services are available via telehealth for eligible individuals in Maryland.
            This means you can receive therapy and medication management from the comfort of your home.
          </p>
          <Link to="/services/telehealth" className="text-sm text-blue-700 font-semibold hover:underline inline-flex items-center gap-1">
            Learn about telehealth options <ArrowRight className="w-3 h-3" />
          </Link>
        </section>

        {/* Insurance */}
        <section className="mb-12 bg-indigo-50 rounded-xl p-6 border border-indigo-100">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Insurance & Coverage</h2>
          <p className="text-sm text-gray-700 mb-3">
            SDORA Wellbeing accepts Medicaid, Maryland HealthChoice managed care organizations, and most major insurance plans.
            We are committed to making depression treatment accessible. Contact us to verify your coverage.
          </p>
          <Link to="/insurance" className="text-sm text-indigo-700 font-semibold hover:underline inline-flex items-center gap-1">
            View accepted insurance plans <ArrowRight className="w-3 h-3" />
          </Link>
        </section>

        {/* CTA */}
        <section className="text-center bg-gray-50 rounded-xl p-8 border border-gray-200" data-testid="depression-cta">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Start Your Path to Recovery</h2>
          <p className="text-sm text-gray-600 mb-4">
            Depression does not have to define your life. Call us today or submit a referral to take the first step.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:+18777725150" className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              <Phone className="w-4 h-4" /> (877) 772-5150
            </a>
            <Link to="/get-started" className="inline-flex items-center gap-2 bg-white text-indigo-600 border border-indigo-200 px-6 py-3 rounded-lg font-semibold hover:bg-indigo-50 transition-colors">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default DepressionTreatmentPage;
